import React, { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Calendar, User, Eye, AlertCircle } from "lucide-react";
import { trashService, DeletedTerm } from "../../services/trashService";
import { formatDateTime } from "../../utils/dateFormatter";
import TrashDetailModal from "../TrashDetailModal";
import "../../styles/components/deleted-terms.css";

const DeletedTerms: React.FC = () => {
  const [terms, setTerms] = useState<DeletedTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTerms, setSelectedTerms] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedTerm, setSelectedTerm] = useState<DeletedTerm | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadTerms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trashService.getDeletedTerms(page, limit);
      setTerms(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const handleRestore = async (termId: number) => {
    try {
      await trashService.restoreTerm(termId);
      setTerms(terms.filter(term => term.id !== termId));
      setSelectedTerms(prev => prev.filter(id => id !== termId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục điều khoản');
    }
  };

  const handlePermanentDelete = async (termId: number) => {
    try {
      await trashService.permanentDeleteTerm(termId);
      setTerms(terms.filter(term => term.id !== termId));
      setSelectedTerms(prev => prev.filter(id => id !== termId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn điều khoản');
    }
  };

  const handleBulkRestore = async () => {
    try {
      await trashService.bulkRestoreTerms(selectedTerms);
      setTerms(terms.filter(term => !selectedTerms.includes(term.id)));
      setSelectedTerms([]);
      setTotal(prev => prev - selectedTerms.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục các điều khoản');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await trashService.bulkDeleteTerms(selectedTerms);
      setTerms(terms.filter(term => !selectedTerms.includes(term.id)));
      setSelectedTerms([]);
      setTotal(prev => prev - selectedTerms.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn các điều khoản');
    }
  };

  const toggleSelectTerm = (termId: number) => {
    setSelectedTerms(prev => 
      prev.includes(termId) 
        ? prev.filter(id => id !== termId)
        : [...prev, termId]
    );
  };

  const selectAll = () => {
    setSelectedTerms(terms.map(term => term.id));
  };

  const deselectAll = () => {
    setSelectedTerms([]);
  };

  const handleViewDetails = (term: DeletedTerm) => {
    setSelectedTerm(term);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTerm(null);
  };

  if (loading) {
    return (
      <div className="trash-loading">
        <div className="trash-loading__spinner"></div>
        <p>Đang tải danh sách điều khoản đã xóa...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-empty">
        <AlertCircle size={48} className="trash-empty__icon" />
        <h3 className="trash-empty__title">Lỗi tải dữ liệu</h3>
        <p className="trash-empty__description">{error}</p>
        <button onClick={loadTerms} className="trash-empty__retry">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="deleted-terms">
      <div className="deleted-terms__header">
        <div className="deleted-terms__info">
          <h3 className="deleted-terms__title">Điều khoản đã xóa ({terms.length})</h3>
          <p className="deleted-terms__description">Danh sách các điều khoản đã bị xóa và có thể khôi phục</p>
        </div>
        
        {terms.length > 0 && (
          <div className="deleted-terms__bulk-actions">
            <button 
              className="deleted-terms__bulk-btn deleted-terms__bulk-btn--restore"
              onClick={handleBulkRestore}
              disabled={selectedTerms.length === 0}
            >
              <RotateCcw size={16} />
              Khôi phục ({selectedTerms.length})
            </button>
            <button 
              className="deleted-terms__bulk-btn deleted-terms__bulk-btn--delete"
              onClick={handleBulkDelete}
              disabled={selectedTerms.length === 0}
            >
              <Trash2 size={16} />
              Xóa vĩnh viễn ({selectedTerms.length})
            </button>
            <button 
              className="deleted-terms__bulk-btn deleted-terms__bulk-btn--select"
              onClick={selectedTerms.length === terms.length ? deselectAll : selectAll}
            >
              {selectedTerms.length === terms.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
            </button>
          </div>
        )}
      </div>

      {terms.length === 0 ? (
        <div className="trash-empty">
          <Trash2 size={48} className="trash-empty__icon" />
          <h3 className="trash-empty__title">Không có điều khoản nào trong thùng rác</h3>
          <p className="trash-empty__description">Tất cả điều khoản đều đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="deleted-terms__list">
          {terms.map((term) => (
            <div key={term.id} className="deleted-terms__item">
              <div className="deleted-terms__checkbox">
                <input
                  type="checkbox"
                  checked={selectedTerms.includes(term.id)}
                  onChange={() => toggleSelectTerm(term.id)}
                />
              </div>
              
              <div className="deleted-terms__content">
                <div className="deleted-terms__item-header">
                  <h4 className="deleted-terms__item-title">{term.title}</h4>
                  <span className="deleted-terms__item-status deleted-terms__item-status--deleted">Đã xóa</span>
                </div>
                
                <div className="deleted-terms__meta">
                  <div className="deleted-terms__meta-item">
                    <User size={14} />
                    <span>Xóa bởi: {term.deletedBy}</span>
                  </div>
                  <div className="deleted-terms__meta-item">
                    <Calendar size={14} />
                    <span>Xóa lúc: {formatDateTime(term.deletedAt)}</span>
                  </div>
                  <div className="deleted-terms__meta-item">
                    <span>Phiên bản: {term.version}</span>
                  </div>
                  <div className="deleted-terms__meta-item">
                    <span>Trạng thái: {term.isActive ? "Hoạt động" : "Tạm dừng"}</span>
                  </div>
                </div>
                
                <div className="deleted-terms__preview">
                  {term.content.substring(0, 200)}...
                </div>
              </div>
              
              <div className="deleted-terms__actions">
                <button 
                  className="deleted-terms__action-btn deleted-terms__action-btn--preview"
                  onClick={() => handleViewDetails(term)}
                  title="Xem chi tiết"
                >
                  <Eye size={16} />
                </button>
                <button 
                  className="deleted-terms__action-btn deleted-terms__action-btn--restore"
                  onClick={() => handleRestore(term.id)}
                  title="Khôi phục"
                >
                  <RotateCcw size={16} />
                </button>
                <button 
                  className="deleted-terms__action-btn deleted-terms__action-btn--delete"
                  onClick={() => handlePermanentDelete(term.id)}
                  title="Xóa vĩnh viễn"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {total > limit && (
        <div className="deleted-terms__pagination">
          <button 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="deleted-terms__pagination-btn"
          >
            Trước
          </button>
          <span className="deleted-terms__pagination-info">
            Trang {page} / {Math.ceil(total / limit)}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="deleted-terms__pagination-btn"
          >
            Sau
          </button>
        </div>
      )}
      
      {/* Detail Modal */}
      <TrashDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        item={selectedTerm}
        type="term"
      />
    </div>
  );
};

export default DeletedTerms;
